/**
 * Notification Feedback System
 * Provides haptic feedback and elegant synthesized audio cues
 * Uses Web Audio API for modern, discrete notification sounds
 */

type NotificationVariant = "default" | "success" | "destructive" | "warning" | "info";

// Haptic patterns for different notification types (in milliseconds)
const HAPTIC_PATTERNS: Record<NotificationVariant, number | number[]> = {
  default: 30,
  success: [20, 40, 20],
  destructive: [40, 60, 40],
  warning: [30, 50, 30],
  info: 25,
};

// Audio context singleton
let audioCtx: AudioContext | null = null;

const getAudioContext = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    try {
      audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch {
      return null;
    }
  }
  if (audioCtx.state === "suspended") {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
};

// Elegant synthesized tone definitions
interface ToneConfig {
  frequencies: number[];
  durations: number[];
  volume: number;
  type: OscillatorType;
  fadeOut: number;
}

const TONE_CONFIGS: Record<NotificationVariant, ToneConfig> = {
  default: {
    frequencies: [880, 1047],
    durations: [0.06, 0.08],
    volume: 0.08,
    type: "sine",
    fadeOut: 0.1,
  },
  success: {
    frequencies: [523, 659, 784],
    durations: [0.06, 0.06, 0.1],
    volume: 0.09,
    type: "sine",
    fadeOut: 0.15,
  },
  destructive: {
    frequencies: [440, 349],
    durations: [0.08, 0.12],
    volume: 0.1,
    type: "triangle",
    fadeOut: 0.12,
  },
  warning: {
    frequencies: [587, 523],
    durations: [0.07, 0.09],
    volume: 0.08,
    type: "sine",
    fadeOut: 0.1,
  },
  info: {
    frequencies: [784, 880],
    durations: [0.05, 0.07],
    volume: 0.06,
    type: "sine",
    fadeOut: 0.12,
  },
};

/**
 * Play an elegant synthesized notification tone
 */
const playSynthTone = (variant: NotificationVariant) => {
  const ctx = getAudioContext();
  if (!ctx) return;

  const config = TONE_CONFIGS[variant];
  let startTime = ctx.currentTime;

  config.frequencies.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = config.type;
    osc.frequency.setValueAtTime(freq, startTime);

    const duration = config.durations[i];
    gain.gain.setValueAtTime(config.volume, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration + config.fadeOut);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(startTime);
    osc.stop(startTime + duration + config.fadeOut + 0.01);

    startTime += duration;
  });
};

/**
 * Play haptic feedback if supported by the device
 */
const playHapticFeedback = (pattern: number | number[]) => {
  if (!("vibrate" in navigator)) return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // Haptic not available
  }
};

/**
 * Trigger complete notification feedback (haptic + audio)
 */
export const triggerNotificationFeedback = (
  variant: NotificationVariant = "default",
  enableSound: boolean = true,
  enableHaptic: boolean = true
) => {
  if (enableHaptic) {
    playHapticFeedback(HAPTIC_PATTERNS[variant]);
  }
  if (enableSound) {
    playSynthTone(variant);
  }
};

/**
 * Preload audio context (call on first user interaction)
 */
export const preloadNotificationSounds = () => {
  getAudioContext();
};

export const isHapticSupported = (): boolean => "vibrate" in navigator;
export const isAudioSupported = (): boolean => typeof AudioContext !== "undefined" || typeof (window as any).webkitAudioContext !== "undefined";

// Preload audio context after first user interaction
if (typeof window !== "undefined") {
  const initAudio = () => {
    getAudioContext();
    window.removeEventListener("click", initAudio);
    window.removeEventListener("touchstart", initAudio);
  };
  window.addEventListener("click", initAudio, { once: true });
  window.addEventListener("touchstart", initAudio, { once: true });
}
